import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Check, ChevronDown, ChevronRight, Copy, Loader2, Mail, Trash2, TriangleAlert } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { copyText } from "@/lib/clipboard";

type OrgRole = "owner" | "admin" | "member";
type ProjectRole = "admin" | "deploy" | "view";

interface Member {
  userId: string;
  email: string;
  role: OrgRole;
  joinedAt: string;
}

interface Invite {
  id: string;
  email: string | null;
  role: "admin" | "member";
  expiresAt: string;
  usedAt: string | null;
  usedByUserId: string | null;
}

/**
 * T117 + T118 — org members + invites in one page (org Settings → Members).
 * Owners can edit roles + invite; admins can invite + manage non-owner roles;
 * members get a read-only view.
 */
export function OrgMembers() {
  const { org } = useParams<{ org: string }>();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const members = useQuery({
    queryKey: ["org-members", org],
    queryFn: () => api.get<{ members: Member[] }>(`/orgs/${org}/members`).then((r) => r.members),
  });
  const invites = useQuery({
    queryKey: ["org-invites", org],
    queryFn: () => api.get<{ invites: Invite[] }>(`/orgs/${org}/invites`).then((r) => r.invites),
  });

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: OrgRole }) =>
      api.patch(`/orgs/${org}/members/${userId}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-members", org] }),
  });
  const removeMember = useMutation({
    mutationFn: (userId: string) => api.delete(`/orgs/${org}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-members", org] }),
  });

  function toggleExpanded(userId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  return (
    <div className="space-y-5">
      <Section title="Members" subtitle={`${members.data?.length ?? 0} in this organization`}>
        <div className="divide-y divide-border-muted">
          {members.data?.map((m) => (
            <div key={m.userId}>
              <div className="of-row gap-3">
                {m.role === "member" ? (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(m.userId)}
                    className="shrink-0 text-subtle-foreground hover:text-foreground"
                    title="Manage project access"
                  >
                    {expanded.has(m.userId) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                ) : (
                  <span className="w-3.5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm font-semibold truncate">{m.email}</div>
                  <div className="text-2xs text-subtle-foreground">Joined {new Date(m.joinedAt).toLocaleDateString()}</div>
                </div>
                <select
                  value={m.role}
                  onChange={(e) => changeRole.mutate({ userId: m.userId, role: e.target.value as OrgRole })}
                  disabled={changeRole.isPending}
                  className="h-8 px-2 rounded-md border border-border bg-background text-xs font-mono"
                >
                  <option value="owner">owner</option>
                  <option value="admin">admin</option>
                  <option value="member">member</option>
                </select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-danger hover:bg-danger-subtle"
                  onClick={() => {
                    if (confirm(`Remove ${m.email} from this org?`)) removeMember.mutate(m.userId);
                  }}
                  disabled={removeMember.isPending}
                >
                  {removeMember.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </div>
              {m.role === "member" && expanded.has(m.userId) && (
                <MemberProjectAccess org={org!} userId={m.userId} />
              )}
            </div>
          ))}
        </div>
        {changeRole.error instanceof ApiError && (
          <div className="px-4 py-2 text-xs text-danger">{changeRole.error.message}</div>
        )}
        {removeMember.error instanceof ApiError && (
          <div className="px-4 py-2 text-xs text-danger">{removeMember.error.message}</div>
        )}
      </Section>

      <InviteSection org={org!} invites={invites.data ?? []} />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Invite by email
// ----------------------------------------------------------------------------

function InviteSection({ org, invites }: { org: string; invites: Invite[] }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [result, setResult] = useState<{ acceptUrl: string; emailSent: boolean; emailError?: string; email: string } | null>(null);

  const createInvite = useMutation({
    mutationFn: () =>
      api.post<{ acceptUrl: string; emailSent: boolean; emailError?: string; invite: { email: string } }>(
        `/orgs/${org}/invites`,
        { email, role, expiresInHours: 24 * 7 },
      ),
    onSuccess: (r) => {
      setResult({ acceptUrl: r.acceptUrl, emailSent: r.emailSent, emailError: r.emailError, email: r.invite.email });
      setEmail("");
      qc.invalidateQueries({ queryKey: ["org-invites", org] });
    },
  });
  const revokeInvite = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${org}/invites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-invites", org] }),
  });

  return (
    <Section title="Invite by email" subtitle="Single-use link, expires in 7 days">
      <div className="px-4 py-3 space-y-3">
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (email) createInvite.mutate();
          }}
        >
          <Input
            type="email"
            required
            placeholder="teammate@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 min-w-[220px]"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as "member" | "admin")}
            className="h-8 px-2 rounded-md border border-border bg-background text-xs font-mono"
          >
            <option value="member">member</option>
            <option value="admin">admin</option>
          </select>
          <Button type="submit" size="sm" variant="primary" disabled={!email || createInvite.isPending}>
            {createInvite.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
            Send invite
          </Button>
        </form>

        {createInvite.error instanceof ApiError && (
          <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">
            {createInvite.error.message}
          </div>
        )}

        {result && <InviteResult result={result} onClose={() => setResult(null)} />}
      </div>

      {invites.length > 0 && (
        <div className="divide-y divide-border-muted">
          {invites.map((inv) => (
            <div key={inv.id} className="of-row gap-3">
              <span className="text-2xs uppercase tracking-wider font-mono bg-subtle border border-border-muted rounded px-1.5 py-0.5">
                {inv.role}
              </span>
              <div className="min-w-0 flex-1">
                {inv.email && <div className="font-mono text-xs truncate">{inv.email}</div>}
                <span className="text-2xs text-muted-foreground">
                  {inv.usedAt
                    ? <>Used {new Date(inv.usedAt).toLocaleString()}</>
                    : <>Expires {new Date(inv.expiresAt).toLocaleString()}</>}
                </span>
              </div>
              {!inv.usedAt && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => revokeInvite.mutate(inv.id)}
                  disabled={revokeInvite.isPending}
                  className="text-danger"
                >
                  {revokeInvite.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function InviteResult({
  result,
  onClose,
}: {
  result: { acceptUrl: string; emailSent: boolean; emailError?: string; email: string };
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={cn(
      "rounded-md border p-3 space-y-2",
      result.emailSent ? "border-success/30 bg-success-subtle/50" : "border-attention/30 bg-attention-subtle/50",
    )}>
      <div className="text-2xs font-semibold text-foreground flex items-center gap-1.5">
        {result.emailSent ? (
          <><Check className="h-3.5 w-3.5 text-success" /> Email sent to {result.email}</>
        ) : (
          <><TriangleAlert className="h-3.5 w-3.5 text-attention" /> Couldn't email {result.email}{result.emailError ? ` — ${result.emailError}` : ""}</>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          readOnly
          value={result.acceptUrl}
          onClick={(e) => (e.target as HTMLInputElement).select()}
          className="flex-1 min-w-0 h-8 px-2 rounded-md border border-border bg-background text-xs font-mono"
        />
        <Button
          size="sm"
          variant="default"
          onClick={async () => {
            await copyText(result.acceptUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy link"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Dismiss</Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Per-member project access (plain "member" role only — owner/admin get
// implicit admin on every project, see packages/shared/src/rbac.ts can()).
// ----------------------------------------------------------------------------

function MemberProjectAccess({ org, userId }: { org: string; userId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["member-project-access", org, userId],
    queryFn: () =>
      api
        .get<{ projects: { id: string; slug: string; role: ProjectRole | null }[] }>(
          `/orgs/${org}/members/${userId}/project-access`,
        )
        .then((r) => r.projects),
  });

  const setRole = useMutation({
    mutationFn: ({ projectSlug, role }: { projectSlug: string; role: ProjectRole | "none" }) =>
      role === "none"
        ? api.delete(`/orgs/${org}/projects/${projectSlug}/members/${userId}`)
        : api.post(`/orgs/${org}/projects/${projectSlug}/members`, { userId, role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["member-project-access", org, userId] }),
  });

  return (
    <div className="bg-subtle/30 border-t border-b border-border-muted px-4 py-3 pl-10">
      <div className="text-2xs uppercase tracking-wider text-subtle-foreground mb-2">Project access</div>
      {q.isLoading && (
        <div className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      )}
      {q.data?.length === 0 && (
        <div className="text-xs text-muted-foreground">No projects in this org yet.</div>
      )}
      <div className="space-y-1.5">
        {q.data?.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3">
            <span className="font-mono text-xs min-w-0 truncate">{p.slug}</span>
            <select
              value={p.role ?? "none"}
              onChange={(e) => setRole.mutate({ projectSlug: p.slug, role: e.target.value as ProjectRole | "none" })}
              disabled={setRole.isPending}
              className="h-7 px-2 rounded-md border border-border bg-background text-2xs font-mono"
            >
              <option value="none">no access</option>
              <option value="view">view</option>
              <option value="deploy">deploy</option>
              <option value="admin">admin</option>
            </select>
          </div>
        ))}
      </div>
      {setRole.error instanceof ApiError && (
        <div className="mt-2 text-xs text-danger">{setRole.error.message}</div>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="of-card">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold">{title}</h3>
        {subtitle && <span className="text-2xs text-subtle-foreground">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}
